import { ReactComponent as LevelBackground } from '@assets/components/panel/zone/level.svg';
import { Box, Field as ChakraField, HStack, Text, VStack } from '@chakra-ui/react';
import ErrorContainer from '@components/common/form/ErrorContainer';
import { MapCustomScrollbar } from '@components/common/layout/MapCustomScrollbar';
import { Checkbox } from '@components/ui/checkbox/Checkbox';
import { InputNumber } from '@components/ui/input/InputNumber';
import { useAppSelector } from '@hooks/redux.hooks';
import { PlatformSelectors } from '@redux/config/config.selectors';
import { Field, FormikErrors, FormikTouched } from 'formik';
import { Polygon } from 'geojson';
import { FormattedMessage, useIntl } from 'react-intl';

import { CoordinatesUnitEnum } from '@/types/commons/commons.types';
import { ZoneFormDisplayedFormatEnum } from '@/types/map.types';

import { OldZoneForm } from './ZoneFormPopup';
import ZonePointsGrid from './ZonePointsGrid';

type Props = {
  touched: FormikTouched<OldZoneForm>;
  errors: FormikErrors<OldZoneForm>;
  values: OldZoneForm;
  unit: CoordinatesUnitEnum;
  points: Polygon;
  setFieldValue: (
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    shouldValidate?: boolean | undefined,
  ) => Promise<void | FormikErrors<OldZoneForm>>;
  setFieldTouched: (
    field: string,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined,
  ) => Promise<void | FormikErrors<OldZoneForm>>;
};

export default function ZoneCoordinatesForm({
  touched,
  errors,
  values,
  unit,
  points,
  setFieldTouched,
  setFieldValue,
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const platforms = useAppSelector(PlatformSelectors.selectAllPlatforms);
  const siteAltitude = platforms[0]?.altitude ?? 0;

  return (
    <MapCustomScrollbar height="100%" margin={1} marginTop={1} marginBottom={1} thumbColor="neutral.600">
      <VStack width="100%" padding={2} paddingRight={3} gap={2} backgroundColor="neutral.900" flexGrow={1}>
        <HStack width="100%" gap={0.25} alignItems="start">
          <Box position="relative" width="64px" flexShrink={0}>
            <LevelBackground style={{ position: 'absolute' }} />
            <VStack zIndex={1} gap={0} paddingTop="14px" paddingLeft="14px" paddingRight="10px">
              <Text textStyle="md" fontWeight="medium" color="neutral.300" zIndex={1}>
                <FormattedMessage id="zone.niv" />
              </Text>
              <Text fontFamily="space" fontFeatureSettings="'liga' off" fontSize="32px" zIndex={1}>
                1
              </Text>
              <Text textStyle="md" fontWeight="medium" color="neutral.300" zIndex={1}>
                <FormattedMessage id="zone.upper" />
              </Text>
            </VStack>
          </Box>
          <VStack width="100%" minHeight="308px" padding={2} gap={2} backgroundColor="neutral.800" alignItems="start">
            <ChakraField.Root id="upperValue" required invalid={!!errors.upperValue}>
              <HStack gap={2}>
                <Text color="neutral.300" textStyle="md" fontWeight="medium" pointerEvents="none">
                  <FormattedMessage
                    id="zone.meter"
                    values={{ label: formatMessage({ id: `zone.${values.displayedFormat}` }) }}
                  />
                </Text>
                <Field
                  as={InputNumber}
                  id="upperValue"
                  width="64px"
                  height="48px"
                  name="upperValue"
                  marginLeft="20px"
                  justifyContent="center"
                  invalid={!!errors.upperValue}
                  onChange={async (value?: number) => {
                    await setFieldTouched('upperValue', true);
                    await setFieldValue('upperValue', value ?? '');
                  }}
                />
              </HStack>
            </ChakraField.Root>
            <ErrorContainer error={errors.upperValue} touched={touched.upperValue} />
            <ZonePointsGrid mapboxPoints={points.coordinates[0]} height={values.upperValue} unit={unit} />
          </VStack>
        </HStack>
        <HStack width="100%" gap={0.25} alignItems="start">
          <Box position="relative" width="64px" flexShrink={0}>
            <LevelBackground style={{ position: 'absolute' }} />
            <VStack zIndex={1} gap={0} paddingTop="14px" paddingLeft="14px" paddingRight="10px">
              <Text textStyle="md" fontWeight="medium" color="neutral.300" zIndex={1}>
                <FormattedMessage id="zone.niv" />
              </Text>
              <Text fontFamily="space" fontFeatureSettings="'liga' off" fontSize="32px" zIndex={1}>
                2
              </Text>
              <Text textStyle="md" fontWeight="medium" color="neutral.300" zIndex={1}>
                <FormattedMessage id="zone.lower" />
              </Text>
            </VStack>
          </Box>
          <VStack width="100%" minHeight="308px" padding={2} gap={2} backgroundColor="neutral.800" alignItems="start">
            <HStack gap={0}>
              <Text color="neutral.300" textStyle="md" fontWeight="medium" pointerEvents="none" width="100%">
                <FormattedMessage
                  id="zone.meter"
                  values={{ label: formatMessage({ id: `zone.${values.displayedFormat}` }) }}
                />
              </Text>
              <ChakraField.Root
                id="lowerValue"
                required={!values.isLowerValueEqualToGroundLevel}
                invalid={!!errors.lowerValue}
                disabled={values.isLowerValueEqualToGroundLevel}
                width="64px"
              >
                <Field
                  as={InputNumber}
                  id="lowerLevelHeight"
                  width="64px"
                  height="48px"
                  name="lowerValue"
                  justifyContent="center"
                  invalid={!!errors.lowerValue}
                  disabled={values.isLowerValueEqualToGroundLevel}
                  onChange={async (value?: number) => {
                    await setFieldTouched('upperValue', true);
                    await setFieldTouched('lowerValue', true);
                    await setFieldValue('lowerValue', value ?? '');
                  }}
                />
              </ChakraField.Root>
              <Checkbox
                checked={values.isLowerValueEqualToGroundLevel}
                marginLeft={2.5}
                label={<FormattedMessage id="zone.groundHeight" />}
                onChange={() => {
                  setFieldValue('isLowerValueEqualToGroundLevel', !values.isLowerValueEqualToGroundLevel);
                  setFieldValue(
                    'lowerValue',
                    values.displayedFormat === ZoneFormDisplayedFormatEnum.HEIGHT ? 0 : siteAltitude,
                  );
                }}
              />
            </HStack>
            <ErrorContainer error={errors.lowerValue} touched={touched.lowerValue} />
            <ZonePointsGrid mapboxPoints={points.coordinates[0]} height={values.lowerValue} unit={unit} isLowerLevel />
          </VStack>
        </HStack>
      </VStack>
    </MapCustomScrollbar>
  );
}
